import { Simplify } from "@jog/types"
import { asText } from "@prismicio/helpers"
import {
    BooleanField,
    ColorField,
    GroupField,
    KeyTextField,
    RichTextField,
    SelectField,
    Slice,
    SliceZone,
    TitleField,
} from "@prismicio/types"

import { typedBoolean } from "../helper"

export type PrismicPdpJson = {
    faslet: boolean
    categoryKeys: string[]
    variantItemCover: string
    // socialProofText: RichTextField
    sizeConflictText: RichTextField
    toolRecommend: RichTextField
    ownSizeToolRecommend: RichTextField
    desktopSortingByName: string[]
    mobileSortingByName: string[]
    sortingByQuality: string[]
    attributes: { attributes: string; label: string; attribute_text: string; capitalize: boolean }[]
    discountLabel: boolean
    promoInformation: {
        color: string
        richTexts: RichTextField[]
    }[]
    sizeConflictTextBgColor: ColorField
    sizeConflictTextColor: ColorField
    // socialProofTextColor: ColorField
    checkmarkBox: { backgroundColor: string; items: string[] }[]
    sustainable: { id: string; items: RichTextField[] }[]
    reviewInfo?: RichTextField
    metaTagTitle: string
    metaTagDescription: string
    metaTagRobots: string
}
export type PrebuildPdpFetch = () => Promise<PrismicPdpJson>

export const transPdp = (data: {
    meta_tag_title: RichTextField
    meta_tag_description: RichTextField
    meta_tag_robots: RichTextField
    faslet_show: BooleanField
    discount_label: BooleanField
    variant_item_cover: SelectField<
        "onmodel-front" | "onmodel-back" | "onmodel-detail" | "onmodel-total" | "flatshots-front",
        "filled"
    >
    review_info?: RichTextField
    // social_proof_text: RichTextField
    // social_proof_text_color: ColorField
    size_conflict_text: RichTextField
    tool_recommend: RichTextField
    size_conflict_text_color: ColorField
    size_conflict_text_bg_color: ColorField
    own_size_tool_recommend: RichTextField
    body: SliceZone<Slice<"checkmark_box", Simplify<{ box_color: ColorField }>, Simplify<{ list_item: TitleField }>>>
    body1: SliceZone<
        Slice<"promo_information", Simplify<{ color: ColorField }>, Simplify<{ rich_text: RichTextField }>>
    >
    body3: SliceZone<
        Slice<
            "sustainable_content",
            Simplify<{
                id: TitleField
            }>,
            Simplify<{
                message: RichTextField
            }>
        >
    >

    category_keys: { category_key: KeyTextField }[]
    attributes: GroupField<
        Simplify<{
            attributes: TitleField
            label: TitleField
            attribute_text: TitleField
            capitalize: BooleanField
        }>
    >
    sorting_by_name: GroupField<Simplify<{ name: KeyTextField }>>
    mobile_sorting_by_name: GroupField<Simplify<{ name: KeyTextField }>>
    sorting_by_quality: GroupField<Simplify<{ quality: KeyTextField }>>
}): PrismicPdpJson => {
    const mobile_sorting_by_name = data.mobile_sorting_by_name.length
        ? data.mobile_sorting_by_name
        : data.sorting_by_name
    return {
        faslet: data.faslet_show,
        categoryKeys: data.category_keys.map((i) => i.category_key || "").filter(typedBoolean),
        variantItemCover: data.variant_item_cover,
        // socialProofText: data.social_proof_text,
        sizeConflictText: data.size_conflict_text,
        toolRecommend: data.tool_recommend,
        desktopSortingByName: data.sorting_by_name?.map((i) => i.name || "").filter(typedBoolean) || [],
        mobileSortingByName: mobile_sorting_by_name?.map((i) => i.name || "").filter(typedBoolean) || [],
        sortingByQuality: data.sorting_by_quality?.map((i) => i.quality || "").filter(typedBoolean) || [],
        attributes:
            data.attributes?.map(({ attributes, label, attribute_text, capitalize }) => ({
                attributes: asText(attributes) || "",
                label: asText(label) || "",
                attribute_text: asText(attribute_text) || "",
                capitalize: capitalize,
            })) || [],
        discountLabel: data.discount_label,
        promoInformation: [...(data.body1 || [])].filter(typedBoolean).map(({ items, primary }) => ({
            color: primary.color || "auto",
            richTexts: items.map((i) => i.rich_text),
        })),
        sizeConflictTextBgColor: data.size_conflict_text_bg_color,
        sizeConflictTextColor: data.size_conflict_text_color,
        // socialProofTextColor: data.social_proof_text_color,
        checkmarkBox: [...(data.body || [])].filter(typedBoolean).map(({ items, primary }) => ({
            backgroundColor: primary.box_color || "",
            items: items.map((i) => asText(i.list_item)).filter(typedBoolean),
        })),
        sustainable: [...(data.body3 || [])]
            .filter(typedBoolean)
            .map(({ items, primary }) => ({ id: asText(primary.id) || "", items: items.map((i) => i.message) }))
            .filter((i) => i.id),
        reviewInfo: data.review_info || [],
        metaTagTitle: asText(data.meta_tag_title) || "",
        metaTagDescription: asText(data.meta_tag_description) || "",
        metaTagRobots: asText(data.meta_tag_robots) || "",
        ownSizeToolRecommend: data.own_size_tool_recommend,
    }
}
